import { CSSProperties } from 'react';
import { ObjectFuncs } from '../js/object.funcs';

function className(generator: (names: string[]) => void): string {
  const names: string[] = [];
  generator(names);
  return names.join(' ');
}

function objectToRules(obj: Record<string, any>): string {
  return Object.entries(ObjectFuncs.shakeOffUndefined(obj))
    .map(([key, value]) => {
      const kebabKey = key.replace(/([A-Z])/g, '-$1');
      return `${kebabKey}: ${value};`;
    })
    .join('');
}

function asAbsoluteContainer() {
  const properties: CSSProperties = {
    position: 'relative',
  };

  return properties;
}

function absolute({
  left,
  top,
  right,
  bottom,
  x,
  y,
  xy,
}: {
  left?: string;
  top?: string;
  right?: string;
  bottom?: string;
  x?: string;
  y?: string;
  xy?: string;
}) {
  const properties: CSSProperties = {
    position: 'absolute',
    left: left ?? x ?? xy,
    top: top ?? y ?? xy,
    right: right ?? x ?? xy,
    bottom: bottom ?? y ?? xy,
  };

  return properties;
}

function size({ x, y, xy }: { x?: string; y?: string; xy?: string }) {
  const properties: CSSProperties = {
    width: x ?? xy,
    height: y ?? xy,
  };

  return properties;
}

function padding({
  left,
  top,
  right,
  bottom,
  x,
  y,
  xy,
}: {
  left?: string;
  top?: string;
  right?: string;
  bottom?: string;
  x?: string;
  y?: string;
  xy?: string;
}) {
  const properties: CSSProperties = {
    paddingLeft: left ?? x ?? xy,
    paddingTop: top ?? y ?? xy,
    paddingRight: right ?? x ?? xy,
    paddingBottom: bottom ?? y ?? xy,
  };

  return properties;
}

function margin({ left, top, right, bottom, x, y, xy }: { left?: string; top?: string; right?: string; bottom?: string; x?: string; y?: string; xy?: string }) {
  const properties: CSSProperties = {
    marginLeft: left ?? x ?? xy,
    marginTop: top ?? y ?? xy,
    marginRight: right ?? x ?? xy,
    marginBottom: bottom ?? y ?? xy,
  };

  return properties;
}

function alignCenter() {
  const properties: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return properties;
}

function eventPassThrough() {
  const properties: CSSProperties = {
    pointerEvents: 'none',
  };

  return properties;
}

type Func<S, T> = (...args: S[]) => T;

function wrapToCss<S>(func: Func<S, any>) {
  return ((...args: any[]) => {
    const result = (func as CallableFunction)(...args);
    return objectToRules(result);
  }) as Func<S, string>;
}

function xCenterAlignInside() {
  const properties: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  };

  return properties;
}

function yCenterAlignInside() {
  const properties: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
  };

  return properties;
}

export const CssFuncs = {
  className,
  js: {
    asAbsoluteContainer,
    absolute,
    size,
    padding,
    margin,
    alignCenter,
    eventPassThrough,
    xCenterAlignInside,
    yCenterAlignInside,
  },
  css: {
    asAbsoluteContainer: wrapToCss(asAbsoluteContainer),
    absolute: wrapToCss(absolute),
    size: wrapToCss(size),
    padding: wrapToCss(padding),
    margin: wrapToCss(margin),
    alignCenter: wrapToCss(alignCenter),
    eventPassThrough: wrapToCss(eventPassThrough),
    xCenterAlignInside: wrapToCss(xCenterAlignInside),
    yCenterAlignInside: wrapToCss(yCenterAlignInside),
  },
};
